import type { TankSpecial } from "../types/tanks";
import { useProgress } from "../stores/useProgress";

/**
 * Poderes passivos dos tanques (specials) — fachada do cliente.
 *
 * As regras de combate (números e fórmulas) vivem em
 * shared/tankSpecialRules.ts, compartilhadas com o servidor Colyseus:
 * nos modos locais (solo, história, BR, GenAI) o cliente as aplica;
 * em multiplayer o servidor as aplica de forma autoritativa a partir do
 * tankId validado contra o catálogo canônico.
 */
export {
  applyShieldReduction,
  getShieldReductionPercent,
  STEALTH_ACTIVATION_MS,
  getStealthDetectionRadius,
  REGEN_DELAY_MS,
  getRegenPerSecond,
  getExplosiveRadius,
  getExplosiveSplashDamage,
  PIERCE_FALLOFF,
  getPierceCount,
  HOMING_ACQUIRE_RADIUS,
  HOMING_CONE_COS,
  getHomingTurnRate,
  REBIRTH_INVULN_MS,
  getRebirthHealthFraction,
  getRebirthHealth,
  BURN_DURATION_MS,
  BURN_TICK_MS,
  getBurnTotalDamage,
  getBurnTickDamage,
  clampSpecialPower,
  resolveTankSpecial,
} from "@shared/tankSpecialRules";

export const TANK_SPECIALS: Record<TankSpecial, { icon: string; color: string }> = {
  shield: { icon: "🛡️", color: "#a855f7" },
  stealth: { icon: "👁️", color: "#8b5cf6" },
  regen: { icon: "💚", color: "#22c55e" },
  explosive: { icon: "💥", color: "#f97316" },
  piercing: { icon: "🎯", color: "#eab308" },
  homing: { icon: "🛰️", color: "#38bdf8" },
  // Assinaturas dos lendários curados do Bestiário
  rebirth: { icon: "🔥", color: "#fb923c" },
  burn: { icon: "🐉", color: "#ef4444" },
};

// ── Lookup do poder do tanque selecionado ────────────────────────────────────
export interface ActiveTankSpecial {
  type: TankSpecial;
  power: number;
}

/**
 * Poder passivo do tanque atualmente selecionado pelo jogador.
 * Fonte única para gameplay local (store, balas, IA) — retorna null se o
 * tanque não tem poder. Em multiplayer o servidor é a autoridade: aplica o
 * poder server-side e sincroniza o resultado (o cliente só renderiza).
 */
export function getPlayerTankSpecial(): ActiveTankSpecial | null {
  const tank = useProgress.getState().getSelectedTank();
  if (!tank?.special) return null;
  return { type: tank.special, power: tank.specialPower ?? 1 };
}
